import { Link, createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

export const Route = createFileRoute('/privacy-policy')({
  component: PrivacyPolicyPage,
})

function PrivacyPolicyPage() {
  return (
    <main className='bg-background min-h-svh py-8 sm:py-10'>
      <div className='mx-auto w-full max-w-4xl px-4 sm:px-6'>
        <Card>
          <CardHeader className='flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
            <div className='space-y-1'>
              <CardTitle className='text-2xl tracking-tight'>
                Privacy Policy
              </CardTitle>
              <p className='text-muted-foreground text-sm'>Lumoo Studio AB</p>
            </div>
            <Link
              to='/sign-in'
              className='text-muted-foreground text-sm underline hover:opacity-80'
            >
              Back to sign in
            </Link>
          </CardHeader>

          <CardContent className='space-y-8 text-sm leading-6 sm:text-base sm:leading-7'>
            <section className='space-y-3'>
              <h2 className='text-lg font-semibold tracking-tight'>
                1. Introduction
              </h2>
              <p>
                At Lumoo (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;),
                we respect your privacy and are committed to protecting your
                personal data. This privacy policy explains how we collect, use,
                store, and protect your personal information when you interact
                with our website, products, and services, and outlines your
                rights under applicable data protection laws.
              </p>
              <p>
                This policy applies to personal data collected through our
                website, applications, and any related services, sales,
                marketing, or events.
              </p>
            </section>

            <section className='space-y-3'>
              <h2 className='text-lg font-semibold tracking-tight'>
                2. Data We Collect
              </h2>
              <p>
                We may collect and process different types of personal data,
                including:
              </p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>
                  <span className='font-medium'>Identity Data</span>: such as
                  your first name, last name, username, or similar identifiers.
                </li>
                <li>
                  <span className='font-medium'>Contact Data</span>: such as
                  your email address, phone number, and postal address.
                </li>
                <li>
                  <span className='font-medium'>Technical Data</span>: including
                  IP address, browser type and version, device information,
                  operating system, time zone, and other technical identifiers.
                </li>
                <li>
                  <span className='font-medium'>Profile Data</span>: including
                  login credentials, preferences, feedback, interests, and
                  survey responses.
                </li>
                <li>
                  <span className='font-medium'>Usage Data</span>: information
                  about how you use our website, products, and services.
                </li>
                <li>
                  <span className='font-medium'>
                    Marketing and Communications Data
                  </span>
                  : your preferences for receiving marketing communications and
                  how you engage with them.
                </li>
              </ul>
            </section>

            <section className='space-y-3'>
              <h2 className='text-lg font-semibold tracking-tight'>
                3. How We Use Your Data
              </h2>
              <p>
                We only process personal data where there is a lawful basis to
                do so. Most commonly, we use your data in the following
                situations:
              </p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>To perform or prepare a contract with you.</li>
                <li>
                  Where it is necessary for our legitimate business interests,
                  provided those interests are not overridden by your rights and
                  freedoms.
                </li>
                <li>To comply with legal or regulatory obligations.</li>
              </ul>
              <p>
                We do not generally rely on consent as a legal basis for
                processing your personal data, except where required (for
                example, for third-party marketing communications). You may
                withdraw your consent at any time by contacting us.
              </p>
            </section>

            <section className='space-y-3'>
              <h2 className='text-lg font-semibold tracking-tight'>
                4. Data Retention
              </h2>
              <p>
                We retain personal data only for as long as necessary to fulfill
                the purposes for which it was collected, including legal,
                regulatory, tax, accounting, or reporting requirements.
              </p>
              <p>
                In some cases, we may retain data for longer if there is a
                complaint or a reasonable prospect of legal claims.
              </p>
            </section>

            <section className='space-y-3'>
              <h2 className='text-lg font-semibold tracking-tight'>
                5. Your Rights Under GDPR
              </h2>
              <p>
                Under applicable data protection laws, you have the right to:
              </p>
              <ul className='list-disc space-y-2 pl-5'>
                <li>Be informed about how your personal data is used.</li>
                <li>Access the personal data we hold about you.</li>
                <li>Request correction of inaccurate or incomplete data.</li>
                <li>
                  Request deletion of your personal data (&quot;right to be
                  forgotten&quot;).
                </li>
                <li>Restrict or object to the processing of your data.</li>
                <li>Request data portability.</li>
                <li>
                  Object to automated decision-making or profiling, where
                  applicable.
                </li>
              </ul>
              <p>
                To exercise any of these rights, please contact us using the
                details below.
              </p>
            </section>

            <section className='space-y-3'>
              <h2 className='text-lg font-semibold tracking-tight'>
                6. Data Security
              </h2>
              <p>
                We implement appropriate technical and organizational measures
                to protect your personal data against loss, misuse, unauthorized
                access, alteration, or disclosure.
              </p>
              <p>
                Access to personal data is limited to individuals who need it
                for legitimate business purposes and who are subject to
                confidentiality obligations.
              </p>
              <p>
                We also have procedures in place to handle suspected data
                breaches and will notify relevant authorities and affected
                individuals where legally required.
              </p>
            </section>

            <section className='space-y-3'>
              <h2 className='text-lg font-semibold tracking-tight'>
                7. Third-Party Links
              </h2>
              <p>
                Our website may contain links to third-party websites, plug-ins,
                or applications. We do not control these third parties and are
                not responsible for their privacy practices.
              </p>
              <p>
                We encourage you to review the privacy policies of any
                third-party websites you visit.
              </p>
            </section>

            <section className='space-y-3'>
              <h2 className='text-lg font-semibold tracking-tight'>
                8. Cookies
              </h2>
              <p>
                You can configure your browser to refuse some or all cookies, or
                to alert you when cookies are being set. Please note that
                disabling cookies may affect the functionality of parts of our
                website.
              </p>
            </section>

            <section className='space-y-3'>
              <h2 className='text-lg font-semibold tracking-tight'>
                9. Changes to This Privacy Policy
              </h2>
              <p>
                We may update this privacy policy from time to time. Any changes
                will be posted on this page, and the &quot;Last updated&quot;
                date will be revised accordingly.
              </p>
              <p>
                We recommend reviewing this policy periodically to stay
                informed.
              </p>
            </section>

            <section className='space-y-3'>
              <h2 className='text-lg font-semibold tracking-tight'>
                10. Contact Us
              </h2>
              <p>
                If you have any questions about this privacy policy or how we
                handle your personal data, please contact us:
              </p>
              <p>
                Email: hello@lumoo.com
                <br />
                Company: Lumoo Studio AB
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </main>
  )
}
